// One course: a hero (thumbnail, title, blurb, length, gate note) with the
// progress ring and a Continue button, then the ordered lesson rows.
import { AcademicCapIcon, PlayIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Button from "../../components/common/Button";
import ProgressRing from "../../components/common/ProgressRing";
import { createRevealSequence } from "../../components/common/Reveal";
import { courseMinutes, courseProgress, getCourse, lessonsForCourse } from "../catalog";
import { useAcademyProgress } from "../progress";
import { GateChip } from "./Chip";
import CourseThumbnail from "./CourseThumbnail";
import { useGateStatus } from "./gates";
import { lessonPath } from "./LessonCard";
import LessonRow from "./LessonRow";

const CoursePage: React.FC = () => {
  const { courseId = "" } = useParams();
  const navigate = useNavigate();
  const course = getCourse(courseId);
  const { progress, isCompleted } = useAcademyProgress();
  const gate = useGateStatus(course?.gate);

  if (!course) return <Navigate to="/academy" replace />;

  const lessons = lessonsForCourse(course.id);
  const p = courseProgress(course.id, progress);
  const nextUp = lessons.find((l) => !isCompleted(l.id)) ?? lessons[0];
  const allDone = lessons.length > 0 && p.done === p.total;
  const enter = createRevealSequence();
  const slot = (extra?: string) => {
    const r = enter();
    return { className: cn(r.className, extra), style: r.style };
  };

  return (
    <div className="space-y-8">
      <header {...slot("flex flex-col md:flex-row md:items-start gap-5")}>
        <CourseThumbnail course={course} size="hero" glyph={lessons[0]?.sceneId ? "watch" : null} />
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-semibold">{course.title}</h1>
          <p className="text-base-content/70 text-sm mt-1 m-0 max-w-2xl">{course.blurb}</p>
          <div className="flex items-center gap-2 mt-3 flex-wrap text-xs text-base-content/60">
            <span>
              {lessons.length} lesson{lessons.length === 1 ? "" : "s"} · {courseMinutes(course.id)} min
            </span>
            {gate && <GateChip label={gate.label} locked={gate.locked} reason={gate.reason} />}
          </div>
          {gate?.locked && <p className="m-0 mt-2 text-xs text-amber-700 dark:text-amber-400">{gate.reason} You can still read every lesson.</p>}
        </div>
        {lessons.length > 0 && (
          <div className="flex items-center gap-4 md:flex-col md:items-end shrink-0">
            <div className="flex items-center gap-3">
              <ProgressRing value={p.pct} size="md" />
              <div className="text-sm">
                <p className="m-0 font-medium">Your progress {p.pct}%</p>
                <p className="m-0 text-xs text-base-content/60 tabular-nums">
                  {p.done} of {p.total} completed
                </p>
              </div>
            </div>
            {nextUp && (
              <Button variant="primary" size="sm" className="px-4" onClick={() => navigate(lessonPath(nextUp))}>
                <PlayIcon className="size-4" /> {allDone ? "Review from the start" : p.done > 0 ? "Continue" : "Start course"}
              </Button>
            )}
          </div>
        )}
      </header>

      {lessons.length === 0 ? (
        <div className="rounded-lg border border-dashed border-base-300 bg-base-100 p-12 text-center">
          <AcademicCapIcon className="h-10 w-10 mx-auto text-base-content/30 mb-3" />
          <p className="font-medium text-base-content">No lessons in this course yet</p>
          <p className="text-sm text-base-content/60">They are being written. Check back soon.</p>
        </div>
      ) : (
        <section>
          <h2 {...slot("m-0 mb-3 text-sm font-semibold text-gray-600 dark:text-base-content/70 uppercase tracking-wider")}>Lessons</h2>
          <ol className="m-0 p-0 list-none space-y-2 max-w-4xl">
            {lessons.map((lesson, i) => (
              <li key={lesson.id} {...slot()}>
                <LessonRow lesson={lesson} course={course} index={i} />
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
};

export default CoursePage;
